import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { BaseInput } from "shared/components/forms/BaseInput";
import { Row } from "views/applications-id-report/components/Row";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { PropertyGeneralForm } from "shared/app/PropertyGeneralForm";
import { SectionCard } from "shared/app/SectionCard";
import { useFormTyped } from "shared/hooks";
import { PartnerFeatures, PropertyKind } from "shared/types";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import * as yup from "yup";
import { useAuthContext } from "providers/Auth";
import { getErrorMessage } from "utils/error";
import { getPropertyKindLabel } from "shared/utils/property-kind-label";
import { AddressValidationStatus } from "shared/app/AddressValidationStatus";
import { RecheckAddressButton } from "shared/app/AddressValidationStatus/RecheckAddressButton";
import { formatPhone } from "shared/utils/format-phone";

const UPDATE_ERROR_MESSAGES: Record<string, string> = {
  single_family_requires_single_unit:
    "Properties with more than one unit cannot be set to Single family. Choose Multi family or Mobile Home instead."
};

const getUpdateErrorMessage = (error: unknown): string => {
  const raw = getErrorMessage(error);
  return UPDATE_ERROR_MESSAGES[raw] ?? `Failed to update property: ${raw}`;
};

const OWNER_CONTACT_FIELDS = [
  {
    field: "property_owner_name",
    label: "Property Owner name",
    id: "property-owner-contact-name",
    placeholder: "Jane Doe"
  },
  {
    field: "property_owner_email",
    label: "Property Owner email",
    id: "property-owner-contact-email",
    placeholder: "owner@example.com"
  },
  {
    field: "property_owner_phone",
    label: "Property Owner phone",
    id: "property-owner-contact-phone",
    placeholder: "5551234567"
  }
] as const;

type OwnerContactField = (typeof OWNER_CONTACT_FIELDS)[number]["field"];

type OwnerContactValues = Partial<Record<OwnerContactField, string | null>>;

const buildOwnerContactPayload = ({
  values,
  initialValues,
  pmsValues
}: {
  values: OwnerContactValues;
  initialValues: OwnerContactValues;
  pmsValues: Property["pms_values"];
}) => {
  const ownerDetails: Partial<Record<OwnerContactField, string>> = {};
  const pmsOverrides: Partial<Record<OwnerContactField, boolean>> = {};

  OWNER_CONTACT_FIELDS.forEach(({ field }) => {
    const value = (values[field] ?? "").trim();
    const initial = (initialValues[field] ?? "").trim();
    if (value === initial) return;

    ownerDetails[field] = value;
    const syncedValue = pmsValues?.[field];
    pmsOverrides[field] = syncedValue ? value === syncedValue : false;
  });

  return {
    ...(Object.keys(ownerDetails).length
      ? { owner_details: ownerDetails }
      : {}),
    ...(Object.keys(pmsOverrides).length ? { pms_overrides: pmsOverrides } : {})
  };
};

const getGeneralSectionValidationSchema = ({
  hasPropertyOwner = false,
  pmsLocked = false
}: {
  hasPropertyOwner: boolean;
  pmsLocked?: boolean;
}) => {
  return yup.object().shape({
    propertyId: yup.number().required(),
    name: yup.string().required("Property name is required"),
    owner_name:
      hasPropertyOwner && !pmsLocked
        ? yup.string().required("Owner name is required")
        : yup.string().nullable().optional(),
    property_owner_name: yup.string().nullable().optional(),
    property_owner_email: yup
      .string()
      .email("Must be a valid email address")
      .nullable()
      .optional(),
    property_owner_phone: yup.string().nullable().optional(),
    address1: yup.string().required("Street address is required"),
    address2: yup.string().optional(),
    city: yup.string().required("City is required"),
    state_or_province: yup.string().required("State is required"),
    postal_code: yup
      .string()
      .required("Zip is required")
      .min(5, "Zip code must be at least 5 characters")
      .max(10, "Zip code must be less than or equal to 10 characters"),
    country: yup.string().optional()
  });
};

interface EditGeneralFormProps {
  onClose: () => void;
  pmsLocked?: boolean;
  pmsValues?: Property["pms_values"];
  initialValues: {
    propertyId: number;
    name: string;
    kind: PropertyKind | null;
    address1: string;
    address2?: string;
    state_or_province: string;
    city: string;
    postal_code: string;
    country: string;
    owner_name?: string | null;
    description?: string;
    external_id?: string;
    property_owner_name?: string | null;
    property_owner_email?: string | null;
    property_owner_phone?: string | null;
  };
}

const EditGeneralForm = ({
  onClose,
  initialValues,
  pmsLocked = false,
  pmsValues
}: EditGeneralFormProps) => {
  const queryClient = useQueryClient();
  const { partnerFeatures, type } = useAuthContext();
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const isOwnerLeads = type.isOwnerLeads;
  const { register, handleChange, onSubmit, values } = useFormTyped(
    initialValues,
    getGeneralSectionValidationSchema({ hasPropertyOwner, pmsLocked })
  );

  const { mutate: updateProperty, isPending } = useMutation({
    mutationFn: (values: EditGeneralFormProps["initialValues"]) => {
      const ownerContactPayload = isOwnerLeads
        ? buildOwnerContactPayload({ values, initialValues, pmsValues })
        : {};

      if (pmsLocked)
        return addressApi.editProperty({
          propertyId: values.propertyId,
          kind: values.kind,
          ...ownerContactPayload
        });

      return addressApi.editProperty({
        propertyId: values.propertyId,
        name: values.name,
        kind: values.kind,
        owner_name: values.owner_name ?? undefined,
        address: {
          address1: values.address1,
          address2: values.address2,
          state_or_province: values.state_or_province,
          city: values.city,
          postal_code: values.postal_code,
          country: values.country
        },
        description: values.description,
        external_id: values.external_id,
        ...ownerContactPayload
      });
    },
    onSuccess: () => {
      toast.success("Property updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(initialValues.propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(getUpdateErrorMessage(error));
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (values) => {
      updateProperty(values);
    },
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <PropertyGeneralForm
      isDefaultProperty={!initialValues.kind}
      pmsLocked={pmsLocked}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      handleChange={handleChange as any}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      register={register as any}
      extraSections={
        <Show if={isOwnerLeads}>
          <div className="grid grid-cols-2 gap-4 py-4">
            {OWNER_CONTACT_FIELDS.map(({ field, label, id, placeholder }) => {
              const syncedValue = pmsValues?.[field];
              const isEdited =
                Boolean(syncedValue) &&
                (values[field] ?? "").trim() !== (syncedValue ?? "").trim();

              return (
                <div key={field} className="w-full space-y-1">
                  <BaseInput
                    id={id}
                    label={label}
                    placeholder={placeholder}
                    {...register(field)}
                  />
                  <Show if={Boolean(syncedValue)}>
                    {isEdited ? (
                      <button
                        type="button"
                        className="text-sm font-medium text-primary-500"
                        onClick={() => handleChange(field)(syncedValue ?? "")}
                      >
                        Revert to synced value
                      </button>
                    ) : (
                      <p className="text-sm text-content-300">
                        Synced from your property management system
                      </p>
                    )}
                  </Show>
                </div>
              );
            })}
          </div>
        </Show>
      }
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
        />
      }
    />
  );
};

export const GeneralSection = ({ property }: { property: Property }) => {
  const { partnerFeatures, type } = useAuthContext();
  const propertyKindLabel = getPropertyKindLabel(property.kind);
  const { Property: PropertyLabel } = useUnitsPropertiesDictionary();
  const [isEditing, setIsEditing] = useState(false);
  const firstUnit = property.first_unit ?? property.units?.[0];
  const description =
    property.kind === PropertyKind.SingleFamily
      ? firstUnit?.description
      : property.description;
  const hasPropertyOwner = partnerFeatures.includes(
    PartnerFeatures.PropertyOwner
  );
  const isOwnerLeads = type.isOwnerLeads;
  const ownerDetails = property.owner_details;
  const ownerPhone = ownerDetails?.property_owner_phone;

  const pmsLocked = Boolean(property.pms_id);

  if (isEditing)
    return (
      <EditGeneralForm
        onClose={() => setIsEditing(false)}
        pmsLocked={pmsLocked}
        pmsValues={property.pms_values}
        initialValues={{
          propertyId: property.id,
          name: property.name,
          kind: property.kind,
          ...property.address,
          description,
          owner_name: property.owner,
          external_id: property.external_id,
          property_owner_name: ownerDetails?.property_owner_name ?? "",
          property_owner_email: ownerDetails?.property_owner_email ?? "",
          property_owner_phone: ownerDetails?.property_owner_phone ?? ""
        }}
      />
    );

  return (
    <SectionCard
      title="General"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title={`${PropertyLabel} name`} value={property.name} />
        <Show if={hasPropertyOwner}>
          <Row title="Owner" value={property.owner} />
        </Show>
        <Show if={isOwnerLeads}>
          <Row
            title="Property Owner name"
            value={ownerDetails?.property_owner_name}
          />
          <Row
            title="Property Owner email"
            value={ownerDetails?.property_owner_email}
          />
          <Row
            title="Property Owner phone"
            value={
              ownerPhone ? formatPhone(ownerPhone) || ownerPhone : undefined
            }
          />
        </Show>
        <Show if={type.isBoomCRM}>
          <Row
            title={`${PropertyLabel} type`}
            value={propertyKindLabel ?? "-"}
          />
        </Show>
        <Row title="Street address" value={property.address.address1} />
        <Row title="Apartment, suite, etc" value={property.address.address2} />
        <Row title="State" value={property.address.state_or_province} />
        <Row title="City" value={property.address.city} />
        <Row title="Zip" value={property.address.postal_code} />
        <Show if={type.isAddressValidationEnabled}>
          <Row
            title="Address validation"
            value={
              <div className="space-y-2">
                <AddressValidationStatus address={property.address} />
                <RecheckAddressButton propertyId={property.id} />
              </div>
            }
            valueClassName="w-[500px]"
          />
        </Show>
        <Row title="Timezone" value={property.timezone} />
        {property.kind || type.isPmsListingSyncEnabled ? (
          <Row
            title="Description"
            value={description}
            valueClassName="w-[500px]"
          />
        ) : null}
        <Row title={`${PropertyLabel} ID`} value={property.external_id} />
      </div>
    </SectionCard>
  );
};
